import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getStoreConfig } from '../utils/clothingStore';
import { getProductStoreConfig } from '../utils/productStore';

interface StoreConfig {
  type: string;
  name: string;
}

interface UseStoreConfigReturn {
  storeConfig: StoreConfig | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useStoreConfig = (): UseStoreConfigReturn => {
  const { user } = useAuth();
  const [storeConfig, setStoreConfig] = useState<StoreConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStoreConfig = useCallback(async () => {
    if (!user) {
      setStoreConfig(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Verificar primero si es tienda de ropa
      const clothingConfig = await getStoreConfig(user.uid);
      if (clothingConfig) {
        // Mapear el tipo de Firestore al tipo esperado por la app
        const mappedConfig = {
          type: clothingConfig.type === 'tienda-ropa' ? 'clothing' : clothingConfig.type,
          name: clothingConfig.name
        };
        setStoreConfig(mappedConfig);
        console.log('✅ Tienda de ropa detectada:', mappedConfig);
      } else {
        // Si no es tienda de ropa, verificar si es tienda de productos
        const productConfig = await getProductStoreConfig(user.uid);
        if (productConfig) {
          setStoreConfig(productConfig);
          console.log('✅ Tienda de productos detectada:', productConfig);
        } else {
          console.log('❌ No se encontró configuración de tienda');
          setStoreConfig(null);
        }
      }
    } catch (err) {
      console.error('Error verificando configuración de tienda:', err);
      setError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchStoreConfig();
  }, [fetchStoreConfig]);

  return {
    storeConfig,
    loading,
    error,
    refetch: fetchStoreConfig
  };
};
